import { ClientTable } from "../../../components/ui/ClientTable";
import { FormSelect } from "../../../components/ui/select";
import {
  ChevronDown,
  Lock,
  Plus,
  Trash2,
  Zap,
} from "lucide-react";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  fetchCatalogProductById,
  fetchMakesFromProductCatalog,
  fetchPartNumbersByMake,
  getQuotationSystemLines,
} from "../../../services/quotationService";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import { QuotationWorkingSheetMobile } from "../mobile-pages/QuotationWorkingSheetMobile";
import {
  sq,
  localizedFieldProps,
  formatInputNumber,
  supportsInstallation,
  extractQuotationSystemLineItems,
  mapSystemApiLinesToWorkingRows,
  parseCurrency,
  formatCurrency,
  formatCurrencyWhole,
  isIncompleteDecimalInput,
  buildBlankWorkingRow,
  isClientOnlyBlankWorkingRow,
  isClientPlaceholderSystemName,
  rowBelongsToSystem,
  resolveQuotationSystems,
  buildRowFromCatalogProduct,
  mergeRowWithCatalogProduct,
  resolveCatalogProductForRow,
  isDiscountPercentAdjustment,
  sanitizePercentInput,
  sanitizeDiscountRateInput,
  toExcelWholeRate,
  computePreparedWorkingRow,
  computeWorkingTotals,
  CLEARED_BACKEND_AMOUNT_FIELDS,
  BACKEND_AMOUNT_INVALIDATING_KEYS,
  resolveRowCpMultiplier,
} from "./quotationUtils";


function WorkingSheetMakeField({ value, makes, makesLoading, editable, onChange }) {
  if (!editable) {
    return (
      <div className="flex h-8 items-center px-2 text-[11px] leading-4 text-[#34445f]">
        {value || "-"}
      </div>
    );
  }

  return (
    <div className="flex h-8 min-w-0 items-center bg-white px-0.5" title={value || undefined}>
      <FormSelect
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={makesLoading}
        className="h-8 w-full min-w-0 border-0 bg-transparent px-1 text-[11px] leading-4 text-[#34445f] shadow-none outline-hidden disabled:text-[#8ea0bd] [&>svg]:h-3 [&>svg]:w-3 [&>svg]:shrink-0"
        {...localizedFieldProps()}
      >
        <option value="">{makesLoading ? "Loading..." : "Select make"}</option>
        {value && !makes.some((make) => make.name === value) ? (
          <option value={value}>{value}</option>
        ) : null}
        {makes.map((make) => (
          <option key={make.id || make.name} value={make.name}>
            {make.name}
          </option>
        ))}
      </FormSelect>
    </div>
  );
}

function WorkingSheetPartNoField({ value, make, editable, onChange, onProductSelect }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const hydratedPartRef = useRef("");

  useEffect(() => {
    const selectedMake = String(make || "").trim();
    if (!selectedMake) {
      setOptions([]);
      setError("");
      setLoading(false);
      hydratedPartRef.current = "";
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    fetchPartNumbersByMake(selectedMake)
      .then((products) => {
        if (cancelled) return;
        setOptions(products);
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setError(err.message || "Unable to load part numbers.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [make]);

  useEffect(() => {
    if (!onProductSelect) return;
    const partNumber = String(value || "").trim();
    if (!partNumber || !options.length) return;
    const hydrateKey = `${String(make || "").trim()}|${partNumber}`;
    if (hydratedPartRef.current === hydrateKey) return;

    const matched = options.find((item) => String(item.partNumber || "").trim() === partNumber);
    if (!matched) return;

    let cancelled = false;
    hydratedPartRef.current = hydrateKey;

    (async () => {
      let resolved = matched;
      if (matched.id) {
        try {
          const detailed = await fetchCatalogProductById(matched.id);
          if (detailed) resolved = detailed;
        } catch {
          resolved = matched;
        }
      }
      if (!cancelled) onProductSelect(resolved, { replace: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [options, value, make, onProductSelect]);

  async function handleSelect(partNumber) {
    onChange(partNumber);
    if (!partNumber) {
      onProductSelect(null);
      return;
    }

    hydratedPartRef.current = `${String(make || "").trim()}|${partNumber}`;

    const matched = options.find((item) => item.partNumber === partNumber);
    if (!matched) {
      onProductSelect({ partNumber, make }, { replace: true });
      return;
    }

    let resolved = matched;
    if (matched.id) {
      try {
        const detailed = await fetchCatalogProductById(matched.id);
        if (detailed) resolved = detailed;
      } catch {
        resolved = matched;
      }
    }
    onProductSelect(resolved, { replace: true });
  }

  if (!editable) {
    return (
      <div className="flex h-8 items-center px-2 text-[11px] leading-4 text-[#34445f]">
        {value || "-"}
      </div>
    );
  }

  const disabled = !String(make || "").trim() || loading;

  return (
    <div className="flex h-8 min-w-0 items-center bg-white px-0.5" title={error || value || undefined}>
      <FormSelect
        value={value || ""}
        onChange={(event) => handleSelect(event.target.value)}
        disabled={disabled}
        title={error || value || undefined}
        className="h-8 w-full min-w-0 border-0 bg-transparent px-1 text-[11px] leading-4 text-[#34445f] shadow-none outline-hidden disabled:text-[#8ea0bd] [&>svg]:h-3 [&>svg]:w-3 [&>svg]:shrink-0"
        {...localizedFieldProps()}
      >
        <option value="">
          {!String(make || "").trim()
            ? "Select make first"
            : loading
              ? "Loading..."
              : options.length
                ? "Select part no."
                : "No parts found"}
        </option>
        {value && !options.some((item) => item.partNumber === value) ? (
          <option value={value}>{value}</option>
        ) : null}
        {options.map((product) => (
          <option key={product.id || product.partNumber} value={product.partNumber}>
            {product.partNumber}
          </option>
        ))}
      </FormSelect>
    </div>
  );
}

function WorkingSheetTab({
  quotationId,
  quotationType,
  rows,
  setRows,
  editable,
  saving,
  systems = [],
  costPricingMultiplier = "",
  backendSheetTotals = null,
  preferBackendTotals = false,
  onAddSystem,
}) {
  useTranslation();
  const showInstallation = supportsInstallation(quotationType);
  const [makes, setMakes] = useState([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [makesError, setMakesError] = useState("");
  const [loadedSystems, setLoadedSystems] = useState({});
  const [expandedSystems, setExpandedSystems] = useState({});
  const [loadingSystems, setLoadingSystems] = useState({});
  const [systemLoadErrors, setSystemLoadErrors] = useState({});
  const loadedSystemsRef = useRef({});
  const systemInflightRef = useRef({});
  const sheetScrollRef = useRef(null);
  const [sheetViewportWidth, setSheetViewportWidth] = useState(0);

  useEffect(() => {
    const node = sheetScrollRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const syncWidth = () => setSheetViewportWidth(node.clientWidth);
    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadedSystemsRef.current = {};
    systemInflightRef.current = {};
    setLoadedSystems({});
    setExpandedSystems({});
    setLoadingSystems({});
    setSystemLoadErrors({});
  }, [quotationId]);

  useEffect(() => {
    let active = true;
    setMakesLoading(true);
    setMakesError("");

    fetchMakesFromProductCatalog()
      .then(({ makes: nextMakes }) => {
        if (!active) return;
        setMakes(nextMakes);
        if (!nextMakes.length) {
          setMakesError("No Make values returned by the product catalogue.");
        }
      })
      .catch((error) => {
        if (!active) return;
        setMakes([]);
        setMakesError(error.message || "Unable to load Make options from product catalogue.");
      })
      .finally(() => {
        if (active) setMakesLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const catalogRowKeys = useMemo(
    () =>
      rows
        .map((row) =>
          [row.id, row.productId, row.partNo, row.make]
            .map((value) => String(value || "").trim())
            .join("|")
        )
        .join(";;"),
    [rows]
  );
  const hydratedCatalogKeysRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const targets = rows.filter((row) => {
        const hasProductId = Boolean(String(row.productId || "").trim());
        const hasPartAndMake =
          Boolean(String(row.partNo || "").trim()) && Boolean(String(row.make || "").trim());
        if (!hasProductId && !hasPartAndMake) return false;
        const key = [row.id, row.productId, row.partNo, row.make]
          .map((value) => String(value || "").trim())
          .join("|");
        return !hydratedCatalogKeysRef.current.has(key);
      });
      if (!targets.length) return;

      // One resolve per distinct productId in this batch; part/make-only rows stay unique.
      const resolveByProductId = new Map();
      const resolved = await Promise.all(
        targets.map(async (row) => {
          const productId = String(row.productId || "").trim();
          let product = null;
          if (productId) {
            if (resolveByProductId.has(productId)) {
              product = await resolveByProductId.get(productId);
            } else {
              const pending = resolveCatalogProductForRow(row);
              resolveByProductId.set(productId, pending);
              product = await pending;
            }
          } else {
            product = await resolveCatalogProductForRow(row);
          }
          return product ? { rowId: row.id, row, product } : null;
        })
      );

      if (cancelled) return;

      const updates = resolved.filter(Boolean);
      if (!updates.length) return;

      updates.forEach(({ row, product }) => {
        const beforeKey = [row.id, row.productId, row.partNo, row.make]
          .map((value) => String(value || "").trim())
          .join("|");
        hydratedCatalogKeysRef.current.add(beforeKey);
        const afterProductId = String(row.productId || product?.id || "").trim();
        const afterPartNo = String(row.partNo || product?.partNumber || "").trim();
        const afterMake = String(row.make || product?.make || product?.manufacturer || "").trim();
        const afterKey = [row.id, afterProductId, afterPartNo, afterMake].join("|");
        hydratedCatalogKeysRef.current.add(afterKey);
      });

      setRows((current) =>
        current.map((row) => {
          const match = updates.find((item) => item.rowId === row.id);
          if (!match) return row;
          return mergeRowWithCatalogProduct(row, match.product);
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogRowKeys, setRows]);

  const workingColumns = [
    { key: "slNo", label: "Sl No", editable: true, width: 44, cellClassName: "w-[44px] min-w-[44px] max-w-[44px]" },
    { key: "description", label: "Description", editable: true, width: 320, cellClassName: "w-[320px] min-w-[320px] max-w-[320px]" },
    { key: "make", label: "Make", editable: true, width: 112, cellClassName: "w-[112px] min-w-[112px] max-w-[112px]" },
    { key: "partNo", label: "Part No", editable: true, width: 108, cellClassName: "w-[108px] min-w-[108px] max-w-[108px]" },
    { key: "uom", label: "Uom", editable: true, width: 48, cellClassName: "w-[48px] min-w-[48px] max-w-[48px]" },
    { key: "qty", label: "Qty", editable: true, numeric: true, width: 48, cellClassName: "w-[48px] min-w-[48px] max-w-[48px]" },
    { key: "listPrice", label: "List Price", editable: true, numeric: true, width: 80, cellClassName: "w-[80px] min-w-[80px] max-w-[80px]" },
    { key: "netPrice", label: "Net Price", editable: true, numeric: true, width: 72, cellClassName: "w-[72px] min-w-[72px] max-w-[72px]" },
    { key: "discount", label: <>Special<br/> Disc%</>, editable: true, numeric: true, width: 68, cellClassName: "w-[68px] min-w-[68px] max-w-[68px]" },
    { key: "initialUnitRate", label: <>Input<br/> Supp. Rate</>, editable: true, numeric: true, width: 80, cellClassName: "w-[80px] min-w-[80px] max-w-[80px]" },
    { key: "initialAmount", label: <>Input<br/> Supp. Amt</>, readonly: true, shaded: true, width: 80, cellClassName: "w-[80px] min-w-[80px] max-w-[80px]" },
    { key: "adjustmentType", label: <>Mult/Disc<br/> Value</>, editable: true, width: 88, cellClassName: "w-[88px] min-w-[88px] max-w-[88px]" },
    { key: "adjustmentRate", label: <>Mult/Disc<br/> Rate</>, editable: true, numeric: true, width: 64, cellClassName: "w-[64px] min-w-[64px] max-w-[64px]" },
    { key: "unitRate", label: <>Selling<br/> Supp. Rate</>, editable: true, numeric: true, width: 88, cellClassName: "w-[88px] min-w-[88px] max-w-[88px]" },
    { key: "amount", label: <>Selling<br/> Supp. Amt</>, readonly: true, accent: true, width: 88, cellClassName: "w-[88px] min-w-[88px] max-w-[88px]" },
    ...(showInstallation ? [{ key: "installationPerUnit", label: "Install/U", editable: true, numeric: true, width: 64, cellClassName: "w-[64px] min-w-[64px] max-w-[64px]" }] : []),
    ...(showInstallation ? [{ key: "inputInstallationAmount", label: "Install Amt", readonly: true, shaded: true, width: 72, cellClassName: "w-[72px] min-w-[72px] max-w-[72px]" }] : []),
    ...(showInstallation ? [{ key: "installationMultiplier", label: "Install Mult.", editable: true, numeric: true, width: 64, cellClassName: "w-[64px] min-w-[64px] max-w-[64px]" }] : []),
    ...(showInstallation ? [{ key: "sellingInstallationRate", label: <>Selling<br/> Install Rate</>, readonly: true, accent: true, width: 88, cellClassName: "w-[88px] min-w-[88px] max-w-[88px]" }] : []),
    ...(showInstallation ? [{ key: "installationAmount", label: <>Selling<br/> Install Amt</>, readonly: true, accent: true, width: 88, cellClassName: "w-[88px] min-w-[88px] max-w-[88px]" }] : []),
    { key: "remarks", label: "Remarks", editable: true, width: 84, cellClassName: "w-[84px] min-w-[84px] max-w-[84px]" },
  ];
  const actionColumnWidth = 40;
  const workingSheetTableMinWidth =
    workingColumns.reduce((sum, column) => sum + Number(column.width || 0), 0) + actionColumnWidth;
  const workingSheetCellPad = "px-1.5";
  const descriptionVisibleRows = 5;
  const descriptionCellHeightClass = "h-24"; // ~5 lines at text-[11px]/leading-4 before scroll

  function getCellTone(column) {
    if (column.accent) return "bg-[#EDF2F8] font-semibold text-[#2944a8]";
    if (column.shaded) return "bg-[#f4f7fc] text-[#6a7f9f]";
    if (column.warning) return "bg-white text-[#34445f]";
    return "bg-white text-[#34445f]";
  }

  function updateRow(rowId, key, value) {
    const clearBackendAmounts = BACKEND_AMOUNT_INVALIDATING_KEYS.has(key)
      ? CLEARED_BACKEND_AMOUNT_FIELDS
      : null;
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        if (key === "adjustmentType") {
          const nextType = value === "Discount %" ? "Discount %" : "Multiplier";
          const cpMultiplier = resolveRowCpMultiplier(row, costPricingMultiplier);
          if (nextType === "Multiplier") {
            return {
              ...row,
              ...clearBackendAmounts,
              adjustmentType: "Multiplier",
              adjustmentRate: cpMultiplier,
              adjustmentRateManual: false,
              // Keep the C+P multiplier as the Mult/Disc Rate source of truth.
              multiplier: cpMultiplier,
              cpMultiplier: cpMultiplier || row.cpMultiplier || "",
              unitRate: "",
              unitRateManual: false,
              // Same C+P default for Installation Multiplier when not manually set.
              ...(supportsInstallation(quotationType) && !row.installationMultiplierManual
                ? { installationMultiplier: cpMultiplier || row.installationMultiplier || "" }
                : {}),
            };
          }
          return {
            ...row,
            ...clearBackendAmounts,
            adjustmentType: "Discount %",
            adjustmentRate: formatInputNumber(parseCurrency(row.discount) || 0),
            adjustmentRateManual: false,
            // Preserve C+P multiplier while on Discount so Multiplier can restore it.
            multiplier: cpMultiplier || row.multiplier || "",
            cpMultiplier: cpMultiplier || row.cpMultiplier || "",
            unitRate: "",
            unitRateManual: false,
          };
        }
        if (key === "adjustmentRate") {
          const nextRate = isDiscountPercentAdjustment(row.adjustmentType)
            ? sanitizeDiscountRateInput(value)
            : value;
          return {
            ...row,
            ...clearBackendAmounts,
            adjustmentRate: nextRate,
            adjustmentRateManual: true,
            // Manual Mult/Disc Rate edits must not overwrite the C+P multiplier source.
            multiplier: isDiscountPercentAdjustment(row.adjustmentType)
              ? (resolveRowCpMultiplier(row, costPricingMultiplier) || row.multiplier || "")
              : nextRate,
            unitRate: "",
            unitRateManual: false,
          };
        }
        if (key === "installationMultiplier") {
          return {
            ...row,
            ...clearBackendAmounts,
            installationMultiplier: value,
            installationMultiplierManual: true,
          };
        }
        if (key === "unitRate") {
          return {
            ...row,
            ...clearBackendAmounts,
            unitRate: value,
            unitRateManual: true,
          };
        }
        if (key === "listPrice" || key === "netPrice" || key === "discount") {
          return {
            ...row,
            ...clearBackendAmounts,
            [key]: value,
            initialUnitRate: "",
            initialUnitRateManual: false,
            unitRate: "",
            unitRateManual: false,
            ...(isDiscountPercentAdjustment(row.adjustmentType) && !row.adjustmentRateManual
              ? { adjustmentRate: "" }
              : {}),
          };
        }
        if (key === "initialUnitRate") {
          return {
            ...row,
            ...clearBackendAmounts,
            initialUnitRate: value,
            initialUnitRateManual: true,
            unitRate: "",
            unitRateManual: false,
            ...(isDiscountPercentAdjustment(row.adjustmentType) && !row.adjustmentRateManual
              ? { adjustmentRate: "" }
              : {}),
          };
        }
        return clearBackendAmounts
          ? { ...row, ...clearBackendAmounts, [key]: value }
          : { ...row, [key]: value };
      })
    );
  }

  function updateBrand(rowId, makeName) {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          ...CLEARED_BACKEND_AMOUNT_FIELDS,
          brandId: makeName || "",
          make: makeName || "",
          productId: "",
          partNo: "",
          listPrice: "",
          netPrice: "",
          discount: "",
          initialUnitRate: "",
          initialUnitRateManual: false,
          unitRate: "",
          unitRateManual: false,
        };
      })
    );
  }

  function applyProductToRow(rowId, product, { replace = true } = {}) {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        if (!product) {
          return {
            ...row,
            ...CLEARED_BACKEND_AMOUNT_FIELDS,
            productId: "",
            partNo: "",
            listPrice: "",
            netPrice: "",
            discount: "",
            initialUnitRate: "",
            initialUnitRateManual: false,
            unitRate: "",
            unitRateManual: false,
          };
        }
        return replace
          ? buildRowFromCatalogProduct(row, product)
          : mergeRowWithCatalogProduct(row, product);
      })
    );
  }

  function updatePartNo(rowId, partNo) {
    setRows((current) =>
      current.map((row) => {
        if (row.id !== rowId) return row;
        if (!partNo) {
          return {
            ...row,
            ...CLEARED_BACKEND_AMOUNT_FIELDS,
            partNo: "",
            productId: "",
            listPrice: "",
            netPrice: "",
            discount: "",
            initialUnitRate: "",
            initialUnitRateManual: false,
            unitRate: "",
            unitRateManual: false,
          };
        }
        return {
          ...row,
          ...CLEARED_BACKEND_AMOUNT_FIELDS,
          partNo,
          productId: row.productId,
          listPrice: row.listPrice,
          netPrice: row.netPrice,
          discount: row.discount,
          initialUnitRate: row.initialUnitRate,
          unitRate: row.unitRate,
          unitRateManual: Boolean(row.unitRateManual),
        };
      })
    );
  }

  function addRow(system) {
    setRows((current) => {
      // Mobile: never stack multiple empty lines under the same system (totals / save noise).
      if (
        isMobileLayout &&
        (system?.id || system?.name) &&
        current.some(
          (row) => rowBelongsToSystem(row, system) && isClientOnlyBlankWorkingRow(row)
        )
      ) {
        return current;
      }
      const nextRow = buildBlankWorkingRow(quotationType, system);
      if (!system?.id && !system?.name) return [...current, nextRow];
      const insertAt = current.reduce((lastIndex, row, index) => (
        rowBelongsToSystem(row, system) ? index + 1 : lastIndex
      ), -1);
      if (insertAt < 0) return [...current, nextRow];
      const next = current.slice();
      next.splice(insertAt, 0, nextRow);
      return next;
    });
  }

  function removeRow(rowId) {
    setRows((current) => (current.length > 1 ? current.filter((row) => row.id !== rowId) : current));
  }

  const resolvedSystems = systems.length ? systems : resolveQuotationSystems({}, rows);
  const rowsById = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => map.set(String(row.id), row));
    return map;
  }, [rows]);
  const preparedRows = rows.map((row) =>
    computePreparedWorkingRow(row, quotationType, { costPricingMultiplier })
  );
  const computedTotals = computeWorkingTotals(preparedRows, quotationType);
  const hasBackendSheetTotals = Boolean(
    backendSheetTotals &&
    (
      backendSheetTotals.supplyInputAmount != null ||
      backendSheetTotals.inputInstallationAmount != null ||
      backendSheetTotals.supplySubtotal != null ||
      backendSheetTotals.installationSubtotal != null
    )
  );
  const showSystemHeaders = resolvedSystems.length > 0;
  // Collapsed/unloaded systems are absent from `rows`; do not undercount via partial live math.
  const allSystemsLoaded =
    !showSystemHeaders ||
    resolvedSystems.every((system) =>
      rows.some((row) => rowBelongsToSystem(row, system))
    );
  // Prefer saved GET totals when available: on open rows are empty (lazy systems),
  // after save (preferBackendTotals), while any system is still unloaded, when live
  // installation undercounts saved GET, and while loaded rows still prefer backend amounts.
  const liveUndercountsBackendInstallation =
    supportsInstallation(quotationType) &&
    backendSheetTotals?.installationSubtotal != null &&
    computedTotals.installationSubtotal < Number(backendSheetTotals.installationSubtotal);
  const preferBackendSheetTotals =
    hasBackendSheetTotals &&
    (
      Boolean(preferBackendTotals) ||
      rows.length === 0 ||
      !allSystemsLoaded ||
      liveUndercountsBackendInstallation ||
      rows.every((row) => row.preferBackendAmounts !== false)
    );
  const backendCustomerSellingTotal =
    backendSheetTotals &&
    (backendSheetTotals.supplySubtotal != null || backendSheetTotals.installationSubtotal != null)
      ? (Number(backendSheetTotals.supplySubtotal) || 0) +
        (Number(backendSheetTotals.installationSubtotal) || 0)
      : null;
  const totals = preferBackendSheetTotals
    ? {
        ...computedTotals,
        supplyInputAmount: backendSheetTotals.supplyInputAmount ?? computedTotals.supplyInputAmount,
        inputInstallationAmount: backendSheetTotals.inputInstallationAmount ?? computedTotals.inputInstallationAmount,
        supplySubtotal: backendSheetTotals.supplySubtotal ?? computedTotals.supplySubtotal,
        installationSubtotal: backendSheetTotals.installationSubtotal ?? computedTotals.installationSubtotal,
        supplySellingAmount: backendSheetTotals.supplySubtotal ?? computedTotals.supplySellingAmount,
        sellingInstallationAmount: backendSheetTotals.installationSubtotal ?? computedTotals.sellingInstallationAmount,
        customerSellingTotal: backendCustomerSellingTotal ?? computedTotals.customerSellingTotal,
      }
    : computedTotals;
  const isMobileLayout = useIsMobileLayout();

  const systemSections = resolvedSystems.length
    ? resolvedSystems.map((system) => {
        const sectionRows = preparedRows.filter((row) => rowBelongsToSystem(row, system));
        return {
          ...system,
          rows: sectionRows,
          totals: computeWorkingTotals(sectionRows, quotationType),
        };
      })
    : [{
        id: "default",
        name: "",
        rows: preparedRows,
        totals,
      }];
  const unassignedRows = resolvedSystems.length
    ? preparedRows.filter((row) => !resolvedSystems.some((system) => rowBelongsToSystem(row, system)))
    : [];
  // Mobile never surfaces an "Ungrouped" system — only user-selected FSM systems.
  if (unassignedRows.length && !isMobileLayout) {
    // Keep orphan rows visible on desktop without inventing a hardcoded System name.
    systemSections.push({
      id: "unassigned",
      name: "",
      rows: unassignedRows,
      totals: computeWorkingTotals(unassignedRows, quotationType),
    });
  }

  const columnCount = workingColumns.length + 1;
  const sheetBandStyle = { width: sheetViewportWidth ? `${sheetViewportWidth}px` : "100%" };

  function getSystemSectionKey(section) {
    return String(section?.id || section?.name || "default");
  }

  function isSystemSectionLoaded(section) {
    const key = getSystemSectionKey(section);
    return Boolean(loadedSystems[key] || loadedSystemsRef.current[key]);
  }

  function isSystemSectionExpanded(section) {
    return Boolean(expandedSystems[getSystemSectionKey(section)]);
  }

  function isSystemSectionLoading(section) {
    return Boolean(loadingSystems[getSystemSectionKey(section)]);
  }

  function setSystemSectionExpanded(section, expanded) {
    const key = getSystemSectionKey(section);
    setExpandedSystems((current) => {
      if (Boolean(current[key]) === Boolean(expanded)) return current;
      return { ...current, [key]: Boolean(expanded) };
    });
  }

  async function loadSystemSectionLines(section) {
    const key = getSystemSectionKey(section);
    if (section.id === "unassigned" || section.id === "default" || !section.name) {
      loadedSystemsRef.current[key] = true;
      setLoadedSystems((current) => ({ ...current, [key]: true }));
      setSystemSectionExpanded(section, true);
      return;
    }
    // Mobile: never GET lines for client placeholder labels (Default System / System 1 / …).
    if (isMobileLayout && isClientPlaceholderSystemName(section.name)) {
      loadedSystemsRef.current[key] = true;
      setLoadedSystems((current) => ({ ...current, [key]: true }));
      setSystemSectionExpanded(section, true);
      return;
    }
    if (loadedSystemsRef.current[key]) {
      setSystemSectionExpanded(section, true);
      return;
    }
    if (systemInflightRef.current[key]) {
      await systemInflightRef.current[key];
      setSystemSectionExpanded(section, true);
      return;
    }
    if (!quotationId) {
      setSystemLoadErrors((current) => ({
        ...current,
        [key]: "Quotation ID is required to load system lines.",
      }));
      return;
    }

    // Mobile: if this system already has local lines (just added), treat as loaded — same as
    // desktop after expand has populated rows. Avoid a redundant get/{id}/{systemName}.
    if (
      isMobileLayout &&
      rows.some((row) => rowBelongsToSystem(row, section) && !isClientOnlyBlankWorkingRow(row))
    ) {
      loadedSystemsRef.current[key] = true;
      setLoadedSystems((current) => ({ ...current, [key]: true }));
      setSystemSectionExpanded(section, true);
      return;
    }

    setLoadingSystems((current) => ({ ...current, [key]: true }));
    setSystemLoadErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });

    const pending = (async () => {
      const response = await getQuotationSystemLines(quotationId, section.name);
      const lineItems = extractQuotationSystemLineItems(response);
      const nextRows = mapSystemApiLinesToWorkingRows(lineItems, section, quotationType, {
        backendDerivedMultiplier: costPricingMultiplier,
      });
      setRows((current) => {
        const retained = current.filter((row) => !rowBelongsToSystem(row, section));
        if (!nextRows.length) {
          // Keep an existing local blank line under this system; do not invent a second one.
          if (retained.some((row) => rowBelongsToSystem(row, section))) {
            return retained;
          }
          return [...retained, buildBlankWorkingRow(quotationType, section)];
        }
        return [...retained, ...nextRows];
      });
      loadedSystemsRef.current[key] = true;
      setLoadedSystems((current) => ({ ...current, [key]: true }));
      setSystemSectionExpanded(section, true);
    })()
      .catch((error) => {
        setSystemLoadErrors((current) => ({
          ...current,
          [key]: error.message || "Failed to load system lines.",
        }));
      })
      .finally(() => {
        delete systemInflightRef.current[key];
        setLoadingSystems((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
      });

    systemInflightRef.current[key] = pending;
    await pending;
  }

  async function handleSystemHeaderClick(section) {
    if (isSystemSectionLoading(section)) return;

    // Already expanded → collapse (keep loaded rows; no API call).
    if (isSystemSectionExpanded(section)) {
      setSystemSectionExpanded(section, false);
      return;
    }

    // Collapsed but previously loaded → expand only (no duplicate API call).
    if (isSystemSectionLoaded(section)) {
      setSystemSectionExpanded(section, true);
      return;
    }

    // First open → expand and load via GET .../get/{id}/{systemName}.
    await loadSystemSectionLines(section);
  }

  async function handleAddRowForSystem(section) {
    const target = section.id === "unassigned" ? {} : section;
    if (section.id !== "unassigned" && section.id !== "default" && section.name) {
      // Mobile: locally added systems are already marked loaded — skip get/{id}/{systemName}.
      if (!(isMobileLayout && isSystemSectionLoaded(section))) {
        await loadSystemSectionLines(section);
      } else {
        setSystemSectionExpanded(section, true);
      }
    } else {
      setSystemSectionExpanded(section, true);
    }
    addRow(target);
  }

  /** Drop auto-seeded blank rows that have no system — they must not become "Ungrouped". */
  function isUngroupedClientBlankRow(row = {}) {
    return (
      isClientOnlyBlankWorkingRow(row) &&
      !String(row.systemName || "").trim() &&
      !String(row.systemId || "").trim()
    );
  }

  // Mobile: never keep a default blank line when no system has been chosen yet.
  useEffect(() => {
    if (!isMobileLayout) return undefined;
    if (resolvedSystems.length > 0) return undefined;
    setRows((current) => {
      const next = current.filter((row) => !isUngroupedClientBlankRow(row));
      return next.length === current.length ? current : next;
    });
    return undefined;
  }, [isMobileLayout, quotationId, resolvedSystems.length, setRows]);

  /** Mobile: add FSM products-services system, expand it, and seed the first blank line. */
  async function handleMobileAddSystem(option) {
    if (typeof onAddSystem !== "function") {
      throw new Error("Add System is not available.");
    }
    const system = {
      id: String(option?.id || option?.productServiceId || "").trim(),
      name: String(option?.name || "").trim(),
    };
    if (!system.id || !system.name) {
      throw new Error("Select a valid system.");
    }

    const alreadyPresent = resolvedSystems.some(
      (entry) =>
        String(entry.id) === system.id ||
        String(entry.name || "").trim().toLowerCase() === system.name.toLowerCase()
    );
    if (!alreadyPresent) {
      await onAddSystem(system);
    }

    const key = getSystemSectionKey(system);
    loadedSystemsRef.current[key] = true;
    setLoadedSystems((current) => ({ ...current, [key]: true }));
    setExpandedSystems((current) => ({ ...current, [key]: true }));
    setSystemLoadErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });

    // Seed exactly one blank line for a newly added system — never duplicate if rows exist.
    setRows((current) => {
      const cleaned = current.filter((row) => !isUngroupedClientBlankRow(row));
      if (cleaned.some((row) => rowBelongsToSystem(row, system))) {
        return cleaned;
      }
      return [...cleaned, buildBlankWorkingRow(quotationType, system)];
    });
  }

  if (isMobileLayout) {
    return (
      <QuotationWorkingSheetMobile
        editable={editable}
        saving={saving}
        showInstallation={showInstallation}
        showSystemHeaders={showSystemHeaders}
        systemSections={systemSections}
        rows={rows}
        makes={makes}
        makesLoading={makesLoading}
        makesError={makesError}
        formatMoney={formatCurrency}
        isSystemSectionExpanded={isSystemSectionExpanded}
        isSystemSectionLoading={isSystemSectionLoading}
        isSystemSectionLoaded={isSystemSectionLoaded}
        systemLoadErrors={systemLoadErrors}
        getSystemSectionKey={getSystemSectionKey}
        onToggleSystem={handleSystemHeaderClick}
        onAddRowForSystem={handleAddRowForSystem}
        onAddSystem={handleMobileAddSystem}
        onUpdateRow={updateRow}
        onRemoveRow={removeRow}
        onUpdateBrand={updateBrand}
        onUpdatePartNo={updatePartNo}
        onApplyProduct={applyProductToRow}
        MakeField={WorkingSheetMakeField}
        PartField={WorkingSheetPartNoField}
        localizedFieldProps={localizedFieldProps}
      />
    );
  }

  return (
    <div className={!editable ? "space-y-2" : ""}>
      {!editable && (
        <div className="flex items-start gap-2.5 rounded-[12px] border border-[#e4eaf4] bg-[#f8fbff] px-3 py-2 text-[#6a7f9f]">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="text-[12px] font-semibold">{sq("readonlyTitle")}</p>
            <p className="mt-0.5 text-[11px] leading-5">{sq("readonlyHint")}</p>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-[12px] border border-[#d8e0ed] bg-white shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)]">
        {makesError || !showSystemHeaders ? (
          <div className="flex items-center justify-end gap-2 px-3 py-1">
            {makesError ? <p className="mr-auto text-[11px] text-[#b35b62]">{makesError}</p> : null}
            {!showSystemHeaders ? (
              <button
                type="button"
                onClick={() => addRow()}
                disabled={!editable || saving}
                className="inline-flex h-7 items-center gap-1 rounded-[8px] bg-[#eef3fb] px-2 text-[11px] font-semibold text-[#2944a8] disabled:cursor-not-allowed disabled:text-[#a6b3d6]"
              >
                <Plus className="h-3.5 w-3.5" />
                {sq("addItem")}
              </button>
            ) : null}
          </div>
        ) : null}

        <div ref={sheetScrollRef} className="overflow-x-auto">
          <ClientTable
            className="w-full table-fixed border-collapse [&_td]:align-middle [&_th]:align-middle"
            style={{ minWidth: `${workingSheetTableMinWidth}px`, width: `${workingSheetTableMinWidth}px` }}
          >
            <colgroup>
              {workingColumns.map((column) => (
                <col key={column.key} style={{ width: `${column.width}px` }} />
              ))}
              <col style={{ width: `${actionColumnWidth}px` }} />
            </colgroup>
            <thead className="sticky top-0 z-4 bg-[#f7faff]">
              <tr className="border-b border-[#d9e1ee] text-left">
                {workingColumns.map((column) => (
                  <th
                    key={column.key}
                    className={`overflow-hidden ${workingSheetCellPad} py-2 text-[10px] font-semibold tracking-normal text-[#96a7c2] ${column.cellClassName || ""}`}
                  >
                    <span className="block truncate whitespace-nowrap" title={typeof column.label === "string" ? column.label : undefined}>
                      {column.label}
                    </span>
                  </th>
                ))}
                <th
                  className={`w-[40px] min-w-[40px] max-w-[40px] overflow-hidden ${workingSheetCellPad} py-2 text-[10px] font-semibold tracking-normal text-[#96a7c2]`}
                >
                  <span className="block truncate whitespace-nowrap">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {systemSections.map((section) => {
                const isNamedSystem = Boolean(String(section.name || "").trim());
                const sectionLoaded =
                  !showSystemHeaders ||
                  !isNamedSystem ||
                  isSystemSectionLoaded(section);
                const sectionExpanded =
                  !showSystemHeaders ||
                  !isNamedSystem ||
                  isSystemSectionExpanded(section);
                const sectionLoading =
                  showSystemHeaders && isNamedSystem && isSystemSectionLoading(section);
                const sectionError = systemLoadErrors[getSystemSectionKey(section)] || "";
                return (
                <Fragment key={section.id || section.name || "rows"}>
                  {showSystemHeaders && isNamedSystem ? (
                    <tr className="border-b border-[#d9e1ee]">
                      <td colSpan={columnCount} className="p-0">
                        <div className="sticky left-0 z-1" style={sheetBandStyle}>
                          <div className="flex h-7 items-center justify-between bg-[#eef3fb] px-3">
                            <button
                              type="button"
                              onClick={() => handleSystemHeaderClick(section)}
                              disabled={sectionLoading}
                              aria-expanded={sectionExpanded}
                              className="flex min-w-0 flex-1 items-center gap-2 text-left transition hover:opacity-90 disabled:cursor-wait"
                            >
                              <ChevronDown
                                className={`h-3.5 w-3.5 shrink-0 text-[#2944a8] transition-transform ${
                                  sectionExpanded ? "rotate-0" : "-rotate-90"
                                }`}
                              />
                              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-[6px] bg-[#dce6ff] text-[#2944a8]">
                                <Zap className="h-3 w-3" />
                              </span>
                              <p className="truncate text-[12px] font-semibold leading-4 text-[#23324c]">
                                {section.name}
                              </p>
                              {sectionLoading ? (
                                <span className="shrink-0 text-[10px] font-medium text-[#7d8fa9]">Loading...</span>
                              ) : null}
                              {!sectionExpanded && !sectionLoading ? (
                                <span className="shrink-0 text-[10px] font-medium text-[#7d8fa9]">
                                  {sectionLoaded ? "Click to expand" : "Click to load"}
                                </span>
                              ) : null}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAddRowForSystem(section)}
                              disabled={!editable || saving || sectionLoading}
                              className="inline-flex h-6 shrink-0 items-center gap-0.5 rounded-[6px] bg-white px-2 text-[10px] font-semibold text-[#2944a8] disabled:cursor-not-allowed disabled:text-[#a6b3d6]"
                            >
                              <Plus className="h-3 w-3" />
                              {sq("addItem")}
                            </button>
                          </div>
                          {sectionError ? (
                            <p className="bg-[#fff6f5] px-3 py-1.5 text-[11px] font-medium text-[#b04d43]">{sectionError}</p>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : null}
              {sectionExpanded
                ? section.rows.map((row) => {
                return (
                <tr
                  key={row.id}
                  className="border-b border-[#edf1f7]"
                >
                  {workingColumns.map((column) => {
                    const calculatedValue = column.key === "initialAmount"
                        ? row.initialAmount
                        : column.key === "inputInstallationAmount"
                          ? row.inputInstallationAmount
                        : column.key === "amount"
                          ? row.amount
                          : column.key === "sellingInstallationRate"
                            ? row.sellingInstallationRate
                          : column.key === "installationAmount"
                            ? row.installationAmount
                            : row[column.key];

                    if (column.key === "description") {
                      return (
                        <td
                          key={column.key}
                          className={`overflow-hidden ${workingSheetCellPad} py-0 align-top ${column.cellClassName || ""}`}
                        >
                          {/* Taller Description cell so more lines show before the textarea scrolls. */}
                          <div className={`flex ${descriptionCellHeightClass} items-start overflow-hidden px-0.5 ${getCellTone(column)}`}>
                            <textarea
                              value={row.description ?? ""}
                              onChange={(event) => updateRow(row.id, "description", event.target.value)}
                              disabled={!editable || saving}
                              rows={descriptionVisibleRows}
                              title={row.description || ""}
                              {...localizedFieldProps()}
                              className={`${descriptionCellHeightClass} w-full resize-none overflow-y-auto overscroll-contain whitespace-pre-wrap border-0 bg-transparent py-1 text-left text-[11px] leading-4 outline-hidden disabled:cursor-not-allowed disabled:text-[#8ea0bd]`}
                            />
                          </div>
                        </td>
                      );
                    }

                    if (column.key === "adjustmentType") {
                      return (
                        <td key={column.key} className={`overflow-hidden ${workingSheetCellPad} py-0 align-top ${column.cellClassName || ""}`}>
                          <FormSelect
                            value={row.adjustmentType === "Discount %" ? "Discount %" : "Multiplier"}
                            disabled={!editable}
                            onChange={(event) => updateRow(row.id, "adjustmentType", event.target.value)}
                            className="h-8 w-full truncate rounded-none border-0 bg-transparent px-1.5 text-[11px] leading-4 text-[#34445f] outline-hidden transition focus:bg-[#f7faff] disabled:cursor-not-allowed"
                          >
                            <option value="Multiplier">Multiplier</option>
                            <option value="Discount %">Discount %</option>
                          </FormSelect>
                        </td>
                      );
                    }

                    if (column.key === "adjustmentRate") {
                      const isDiscountRate = isDiscountPercentAdjustment(row.adjustmentType);
                      return (
                        <td key={column.key} className={`overflow-hidden ${workingSheetCellPad} py-0 align-top ${column.cellClassName || ""}`}>
                          <div className={`flex h-8 items-center gap-1 overflow-hidden px-0.5 ${getCellTone(column)}`}>
                            <input
                              value={
                                isDiscountRate
                                  ? sanitizeDiscountRateInput(row.adjustmentRate)
                                  : sanitizePercentInput(row.adjustmentRate)
                              }
                              onChange={(event) => updateRow(row.id, "adjustmentRate", event.target.value)}
                              disabled={!editable || saving}
                              inputMode="decimal"
                              {...localizedFieldProps()}
                              className="w-full border-0 bg-transparent text-center text-[11px] outline-hidden transition disabled:cursor-not-allowed disabled:text-[#8ea0bd]"
                            />
                            {isDiscountRate ? (
                              <span className="shrink-0 text-[11px] font-semibold text-[#6f82a1]">%</span>
                            ) : null}
                          </div>
                        </td>
                      );
                    }

                    if (column.readonly) {
                      return (
                        <td
                          key={column.key}
                          className={`overflow-hidden ${workingSheetCellPad} py-0 align-top ${column.cellClassName || ""}`}
                        >
                          <div className={`flex h-8 max-w-full items-center justify-center overflow-hidden px-0.5 text-center text-[11px] leading-4 ${column.warning && !String(calculatedValue || "").trim()
                            ? "bg-[#fff8f1] text-[#ca7b30]"
                            : getCellTone(column)}`}>
                            <span className="block w-full truncate whitespace-nowrap" title={String(calculatedValue || "").trim() ? String(calculatedValue) : (column.warning ? sq("noCataloguePrice") : "")}>
                              {String(calculatedValue || "").trim() ? calculatedValue : column.warning ? sq("noCataloguePrice") : "-"}
                            </span>
                          </div>
                        </td>
                      );
                    }

                    if (column.key === "make") {
                      return (
                        <td
                          key={column.key}
                          className={`overflow-hidden ${workingSheetCellPad} py-0 align-top ${column.cellClassName || ""}`}
                        >
                          <WorkingSheetMakeField
                            value={row.make}
                            makes={makes}
                            makesLoading={makesLoading}
                            editable={editable}
                            onChange={(makeName) => updateBrand(row.id, makeName)}
                          />
                        </td>
                      );
                    }

                    if (column.key === "partNo") {
                      return (
                        <td
                          key={column.key}
                          className={`overflow-hidden ${workingSheetCellPad} py-0 align-top ${column.cellClassName || ""}`}
                        >
                          <WorkingSheetPartNoField
                            value={row.partNo}
                            make={row.make}
                            editable={editable}
                            onChange={(partNo) => updatePartNo(row.id, partNo)}
                            onProductSelect={(product, options) => applyProductToRow(row.id, product, options)}
                          />
                        </td>
                      );
                    }

                    return (
                      <td
                        key={column.key}
                        className={`overflow-hidden ${workingSheetCellPad} py-0 align-top ${column.cellClassName || ""}`}
                      >
                        <div className={`flex h-8 items-center overflow-hidden px-0.5 ${getCellTone(column)}`}>
                          <input
                            value={(() => {
                              if (column.key !== "initialUnitRate" && column.key !== "unitRate") {
                                return row[column.key] ?? "";
                              }
                              const source = rowsById.get(String(row.id)) || row;
                              const manual = column.key === "unitRate"
                                ? Boolean(source.unitRateManual)
                                : Boolean(source.initialUnitRateManual);
                              const raw = source[column.key];
                              // Manual / in-progress decimal typing binds to source so mid-replace is not overwritten.
                              // Otherwise show the Excel-rounded prepared rate (same rounding as Amount columns).
                              if (manual || isIncompleteDecimalInput(raw)) {
                                return raw ?? "";
                              }
                              return row[column.key] ?? "";
                            })()}
                            onChange={(event) => updateRow(row.id, column.key, event.target.value)}
                            onBlur={(event) => {
                              if (column.key !== "initialUnitRate" && column.key !== "unitRate") return;
                              const raw = event.target.value;
                              if (isIncompleteDecimalInput(raw)) return;
                              const rounded = toExcelWholeRate(raw);
                              if (rounded === "" || rounded === String(raw).trim()) {
                                // Still mark as manual when user leaves a complete rate field.
                                if (rounded !== "" && (column.key === "unitRate" || column.key === "initialUnitRate")) {
                                  updateRow(row.id, column.key, rounded);
                                }
                                return;
                              }
                              updateRow(row.id, column.key, rounded);
                            }}
                            disabled={!editable || saving}
                            inputMode={column.numeric ? "decimal" : "text"}
                            {...localizedFieldProps()}
                            className={`h-8 w-full border-0 bg-transparent text-[11px] leading-4 outline-hidden transition disabled:cursor-not-allowed disabled:text-[#8ea0bd] ${
                              column.numeric ? "text-center" : "text-left"
                            }`}
                          />
                        </div>
                      </td>
                    );
                  })}
                  <td className={`w-[40px] min-w-[40px] max-w-[40px] overflow-hidden ${workingSheetCellPad} py-0 align-top`}>
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      disabled={!editable || preparedRows.length === 1 || saving}
                      className="grid h-8 w-full place-items-center text-[#8ea0bd] transition hover:bg-[#f1f5fb] hover:text-[#2944a8] disabled:cursor-not-allowed disabled:text-[#c8d2e1]"
                      aria-label="Remove row"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
                );
              })
                : null}
              {sectionExpanded ? (
              <tr className="border-t border-[#d9e1ee] bg-[#f8fbff]">
                <td className={`overflow-hidden ${workingSheetCellPad} py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#6f82a1] ${workingColumns[0]?.cellClassName || ""}`}>
                  {sq("total")}
                </td>
                {workingColumns.slice(1).map((column) => {
                  const totalValue =
                    column.key === "initialAmount" ? formatCurrencyWhole(section.totals.supplyInputAmount)
                      : column.key === "amount" ? formatCurrencyWhole(section.totals.supplySubtotal)
                      : column.key === "inputInstallationAmount" ? formatCurrencyWhole(section.totals.inputInstallationAmount)
                      : column.key === "installationAmount" ? formatCurrencyWhole(section.totals.installationSubtotal)
                      : null;

                  return (
                    <td
                      key={`total-${section.id}-${column.key}`}
                      className={`overflow-hidden ${workingSheetCellPad} py-1.5 text-center text-[11px] font-semibold ${column.cellClassName || ""} ${column.accent ? "text-[#2944a8]" : "text-[#6f82a1]"}`}
                    >
                      <span className="block truncate">{totalValue || "-"}</span>
                    </td>
                  );
                })}
                <td className={`w-[40px] min-w-[40px] max-w-[40px] overflow-hidden ${workingSheetCellPad} py-1.5 text-center text-[11px] font-semibold text-[#6f82a1]`}>
                  -
                </td>
              </tr>
              ) : null}
                </Fragment>
                );
              })}
            </tbody>
          </ClientTable>
        </div>
        <div className="border-t border-[#d9e1ee] bg-[#f6f9fd] px-4 py-3.5 sm:px-5">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.06em] text-[#6f82a1]">{sq("grandTotal")}</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-[12px] border border-[#e4ebf6] bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#96a7c2]">{sq("inputSupplyTotal")}</p>
              <p className="mt-1.5 text-[17px] font-semibold text-[#23324c]">{formatCurrencyWhole(totals.supplyInputAmount)}</p>
            </div>
            <div className="rounded-[12px] border border-[#e4ebf6] bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#96a7c2]">{sq("inputInstallationTotal")}</p>
              <p className="mt-1.5 text-[17px] font-semibold text-[#23324c]">{formatCurrencyWhole(totals.inputInstallationAmount)}</p>
            </div>
            <div className="rounded-[12px] border border-[#e4ebf6] bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#96a7c2]">{sq("sellingSupplyTotal")}</p>
              <p className="mt-1.5 text-[17px] font-semibold text-[#23324c]">{formatCurrencyWhole(totals.supplySubtotal)}</p>
            </div>
            <div className="rounded-[12px] border border-[#e4ebf6] bg-white px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#96a7c2]">{sq("sellingInstallationTotal")}</p>
              <p className="mt-1.5 text-[17px] font-semibold text-[#23324c]">{formatCurrencyWhole(totals.installationSubtotal)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export {
  WorkingSheetTab
};
